
import React from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FileText, CheckCircle, ClipboardList, AlertTriangle, Lightbulb } from "lucide-react";

interface GuidanceDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  guidance: {
    guidance_id: string | null;
    requirement_title: string | null;
    intent: string | null;
    implementation_options: string | null;
    documentation_needed: string | null;
    on_site_auditor_visit: string | null;
    standard_requirement: string | null;
  } | null;
  requirementText: string;
}

const GuidanceDrawer = ({ open, onOpenChange, guidance, requirementText }: GuidanceDrawerProps) => {
  if (!guidance) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[400px] sm:w-[540px] md:w-[600px] overflow-hidden">
        <SheetHeader>
          <SheetTitle>Guidance: {guidance.requirement_title || 'Requirement Guidance'}</SheetTitle>
          <SheetDescription>
            Guidance ID: {guidance.guidance_id || 'N/A'}
          </SheetDescription>
        </SheetHeader>
        
        <ScrollArea className="h-[calc(100vh-120px)] mt-6 pr-4">
          <div className="space-y-6 pb-4">
            <div className="p-4 bg-muted/50 rounded-md">
              <h3 className="font-medium mb-2 flex items-center text-primary">
                <FileText className="h-4 w-4 mr-2" />
                Requirement
              </h3>
              <p className="text-sm">{requirementText}</p>
            </div>

            {guidance.intent && (
              <div>
                <h3 className="font-medium mb-2 flex items-center text-amber-600">
                  <Lightbulb className="h-4 w-4 mr-2" />
                  Intent
                </h3>
                <p className="text-sm whitespace-pre-line">{guidance.intent}</p>
              </div>
            )}

            {guidance.standard_requirement && (
              <div>
                <h3 className="font-medium mb-2 flex items-center text-blue-600">
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Standard Requirement
                </h3>
                <p className="text-sm whitespace-pre-line">{guidance.standard_requirement}</p>
              </div>
            )}

            {guidance.implementation_options && (
              <div>
                <h3 className="font-medium mb-2 flex items-center text-green-600">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Implementation Options
                </h3>
                <p className="text-sm whitespace-pre-line">{guidance.implementation_options}</p>
              </div>
            )}

            {guidance.documentation_needed && (
              <div>
                <h3 className="font-medium mb-2 flex items-center text-violet-600">
                  <ClipboardList className="h-4 w-4 mr-2" />
                  Documentation Needed
                </h3>
                <p className="text-sm whitespace-pre-line">{guidance.documentation_needed}</p>
              </div>
            )}

            {guidance.on_site_auditor_visit && (
              <div>
                <h3 className="font-medium mb-2 flex items-center text-orange-600">
                  <ClipboardList className="h-4 w-4 mr-2" />
                  On-Site Auditor Visit
                </h3>
                <p className="text-sm whitespace-pre-line">{guidance.on_site_auditor_visit}</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export default GuidanceDrawer;
